import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createScene, type SceneHandle } from '@/render/scene'
import { scenario as scenarioById } from '@/core/data'
import { NM_IN_METERS } from '@/core/geometry'
import type { Key } from '@/core/i18n'
import { MONDPHASEN, type ViewMode } from '@/core/settings'
import type { Subject } from './subject'

export { subjectOfCard, type Subject } from './subject'
import type { SignalStep } from '@/core/signals'
import { formatDistance } from '@/core/units'
import { useSettings } from './settings'


export interface ViewerProps {
  subject: Subject
  mode: ViewMode
  /** Auf dem Startbildschirm: nur das Bild, ohne Regler und ohne Anzeige. */
  compact?: boolean
  /**
   * Das gelbe Lichtzeichen der Binnenschifffahrt, während ein Schallzeichen läuft. Die
   * Kennung zählt je Wiedergabe hoch, damit dasselbe Zeichen zweimal hintereinander
   * zweimal blitzt.
   */
  lichtzeichen?: { schritte: SignalStep[]; kennung: number }
}

function applySubject(handle: SceneHandle, subject: Subject): void {
  if (subject.kind === 'buoy') handle.setBuoyId(subject.id)
  else if (subject.kind === 'scenario') handle.setScenarioId(subject.id)
  else handle.setStateId(subject.id)
}

/** Entfernungsregler: logarithmisch von 50 m bis 6,5 sm. */
const MIN_NM = 50 / NM_IN_METERS
const MAX_NM = 6.5

function sliderToNm(v: number): number {
  return MIN_NM * (MAX_NM / MIN_NM) ** v
}

function nmToSlider(nm: number): number {
  return Math.log(nm / MIN_NM) / Math.log(MAX_NM / MIN_NM)
}

const MODI: { id: ViewMode; key: Key }[] = [
  { id: 'night', key: 'mode.night' },
  { id: 'day', key: 'mode.day' },
  { id: 'red', key: 'mode.red' },
]

export function Viewer({ subject, mode, compact = false, lichtzeichen }: ViewerProps) {
  const { t, settings, update } = useSettings()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sceneRef = useRef<SceneHandle | null>(null)
  const [bearing, setBearing] = useState(25)
  const [elevation, setElevation] = useState(0.35)
  const [distanceNm, setDistanceNm] = useState(0.12)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const testMode = new URLSearchParams(window.location.search).has('test')
    const handle = createScene({ canvas, testMode })
    sceneRef.current = handle
    applySubject(handle, subject)
    handle.resize()
    const fit = handle.fitDistanceNm()
    setDistanceNm(fit)
    handle.setDistanceNm(fit)
    // Wer weniger Bewegung will, bekommt weniger Bewegung: keine Dünung, kein Wellengang.
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    handle.setMotion(!testMode && !ruhig)
    if (testMode) {
      window.__nav3d = handle
      handle.renderOnce()
    } else {
      handle.start()
    }
    // Die Rümpfe kommen nach; bis dahin steht die prozedurale Geometrie im Bild.
    void handle.preload().then(() => {
      handle.resize()
      if (testMode) handle.renderOnce()
    })
    const onResize = (): void => handle.resize()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (window.__nav3d === handle) delete window.__nav3d
      handle.dispose()
      sceneRef.current = null
    }
    // Die Szene wird einmal gebaut; Zustandswechsel laufen über setStateId unten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handle = sceneRef.current
    if (!handle) return
    applySubject(handle, subject)
    if (subject.kind === 'scenario') {
      // Der Blick geht zuerst dorthin, wo das andere Fahrzeug steht.
      const look = scenarioById(subject.id).other.bearingDeg ?? 0
      setBearing(look)
      handle.setLookDeg(look)
      return
    }
    const fit = handle.fitDistanceNm()
    setDistanceNm(fit)
    handle.setDistanceNm(fit)
  }, [subject.kind, subject.id])

  useEffect(() => {
    sceneRef.current?.setDayMode(mode === 'day')
    sceneRef.current?.setNightVision(mode === 'red')
  }, [mode])

  useEffect(() => {
    sceneRef.current?.setMondphase(settings.mondphase)
  }, [settings.mondphase])

  useEffect(() => {
    sceneRef.current?.setLichtzeichen(lichtzeichen?.schritte)
  }, [lichtzeichen?.kennung, lichtzeichen?.schritte])

  useEffect(() => {
    // In der Ausweichsituation dreht der Regler den Blick, nicht das Fahrzeug.
    if (subject.kind === 'scenario') sceneRef.current?.setLookDeg(bearing)
    else sceneRef.current?.setBearing(bearing)
  }, [bearing, subject.kind])

  useEffect(() => {
    sceneRef.current?.setElevation(elevation)
  }, [elevation])

  useEffect(() => {
    sceneRef.current?.setDistanceNm(distanceNm)
  }, [distanceNm])

  const drag = useRef<{ x: number; y: number } | null>(null)

  return (
    <section className={`viewer${compact ? ' viewer--compact' : ''}`}>
      <div className="viewer__stage">
        <canvas
          ref={canvasRef}
          className="viewer__canvas"
          data-testid="scene"
          onPointerDown={(e) => {
            drag.current = { x: e.clientX, y: e.clientY }
            e.currentTarget.setPointerCapture(e.pointerId)
          }}
          onPointerMove={(e) => {
            if (!drag.current) return
            const dx = e.clientX - drag.current.x
            const dy = e.clientY - drag.current.y
            drag.current = { x: e.clientX, y: e.clientY }
            setBearing((b) => (((b + dx * 0.4) % 360) + 360) % 360)
            setElevation((v) => Math.min(1, Math.max(0, v - dy * 0.004)))
          }}
          onPointerUp={(e) => {
            drag.current = null
            e.currentTarget.releasePointerCapture(e.pointerId)
          }}
        />
        {/* Die Ansicht gehört an die Szene, nicht in den Kopf der App: sie ändert, was
            man hier sieht, und sonst nichts. Im Kopf war sie eine dritte Leiste über der
            Aufgabe, und auf dem Telefon lag die Aufgabe damit unter dem Rand. */}
        {!compact && (
          <div className="modes viewer__modes" data-testid="modes">
            {MODI.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`modes__item${mode === m.id ? ' modes__item--on' : ''}`}
                data-mode={m.id}
                onClick={() => update({ mode: m.id })}
              >
                {t(m.key)}
              </button>
            ))}
          </div>
        )}
        {/**
          * Die Mondphase gehört ins Bild, nicht darunter.
          *
          * Unter der Szene stand sie auf dem Telefon bei 956 Bildpunkten, das Fenster hat
          * 844: sie war wieder nur mit Scrollen zu erreichen, also wieder hinter einer
          * Tür, und genau davor kam sie ja. Fünf Scheiben zeigen die Phase, wie man sie am
          * Himmel sieht, und jede trägt ihren Namen für alle, die ihn brauchen.
          */}
        {!compact && mode === 'night' && (
          <div className="viewer__mondleiste" data-testid="mondphase-szene">
            {MONDPHASEN.map(([wert, schluessel]) => (
              <button
                key={schluessel}
                type="button"
                className={`mondknopf${settings.mondphase === wert ? ' mondknopf--on' : ''}`}
                data-phase={wert}
                data-testid={`szene-mondphase-${wert}`}
                aria-pressed={settings.mondphase === wert}
                aria-label={t(schluessel as never)}
                title={t(schluessel as never)}
                onClick={() => update({ mondphase: wert })}
              >
                <span
                  className="mondknopf__scheibe"
                  style={{ '--lokal-mond-hell': `${wert * 100}%` } as CSSProperties}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        )}
        {!compact && (
        <div className="viewer__readout" data-testid="readout">
          <span>
            {subject.kind === 'scenario' ? t('viewer.look') : t('viewer.bearing')} {Math.round(bearing)}&deg;
          </span>
          <span>
            {formatDistance(
              subject.kind === 'scenario'
                ? (scenarioById(subject.id).other.distanceNm ?? 1)
                : distanceNm,
              settings.units,
            )}
          </span>
        </div>
        )}
      </div>
      {/* Zwei Regler ohne Erklärung sind zwei Regler, an denen niemand zieht. Zwei
          Schwarmtests haben das gemeldet; der Satz sagt, was man damit lernt. */}
      {!compact && (
      <>
      <p className="viewer__hinweis" data-testid="viewer-hinweis">
        {t(subject.kind === 'scenario' ? 'viewer.hinweis.scenario' : 'viewer.hinweis')}
      </p>
      {/**
        * Die Mondphase gehört an die Szene, nicht in die Einstellungen.
        *
        * Solange sie nur den Mond am Himmel änderte, war sie eine Voreinstellung unter
        * vielen. Seit die Nacht der Phase folgt, ist sie die Schwierigkeit der Aufgabe:
        * bei Vollmond trägt das Streiflicht die Gestalt des Fahrzeugs, bei Neumond bleibt
        * nur, was leuchtet, und das ist die Lage, für die diese App übt. Matthias hat
        * gefragt, wie man sie ändert, und sie hinter der Tür "Einstellungen" nicht
        * gefunden. In den Einstellungen steht sie weiter, beide Stellen zeigen denselben
        * Stand.
        */}
      {mode === 'night' && (
        <p className="viewer__hinweis" data-testid="mondphase-hinweis">
          {t('viewer.mondphase.hinweis')}
        </p>
      )}
      <div className="viewer__controls">
        <label className="control">
          <span className="control__label">
            {subject.kind === 'scenario' ? t('viewer.lookDirection') : t('viewer.bearing')}
          </span>
          <input
            type="range"
            min={0}
            max={359}
            step={1}
            value={Math.round(bearing)}
            data-testid="bearing"
            onChange={(e) => setBearing(Number(e.target.value))}
          />
        </label>
        <label className={`control${subject.kind === 'scenario' ? ' control--off' : ''}`}>
          <span className="control__label">{t('viewer.distance')}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={nmToSlider(distanceNm)}
            data-testid="distance"
            onChange={(e) => setDistanceNm(sliderToNm(Number(e.target.value)))}
          />
        </label>
      </div>
      </>
      )}
    </section>
  )
}
